import {dayInMS, minuteInMS, secondInMS, TimeToString} from "web-vcore";
import moment from "moment";

/** Returns a full `[HH:MM:SS] ` timestamp string (with trailing space) for the given epoch-ms. */
export function FormatFullTimestamp(epochMs: number): string {
	return `[${TimeToString(epochMs, {date: 0})}] `;
}

/** Returns a lite `:+N ` timestamp string (with trailing space) for a chunk, relative to the last full timestamp's epoch-ms. */
export function FormatLiteTimestamp(chunkStartTime: number, lastFullTimestampChunkStartTime: number): string {
	const minuteDiff = Math.floor(chunkStartTime / minuteInMS) - Math.floor(lastFullTimestampChunkStartTime / minuteInMS);
	const colons = ":".repeat(1 + minuteDiff);
	const seconds = Math.floor((chunkStartTime % minuteInMS) / secondInMS);
	return `${colons}${seconds} `;
}

/**
 * Scans `text` up to `cursorPos` for the last timestamp marker — either a full `[HH:MM:SS]` or a
 * lite `:+N` marker — and returns its epoch-ms wall-clock time. Returns null if no timestamp found.
 *
 * Full timestamps are resolved using `entrySleepTime` as the date anchor (midnight-crossing handled).
 * Lite timestamps (`:+N`) are relative to the most recently seen full timestamp in the scan.
 *
 * Full timestamps can appear anywhere in text (no whitespace requirement).
 * Lite timestamps require (whitespace or string boundary) before, so that colon-containing text
 * (like "e.g.:") is not misread as a marker. No constraint is placed on what follows the digits.
 */
export function ParseLastTimestampBeforeCursor(text: string, cursorPos: number, entrySleepTime: number|n): number|null {
	const textBefore = text.slice(0, cursorPos);

	// Scan for both token types in document order by interleaving two regexes.
	//
	// Full:  \[HH:MM:SS\]  — no surrounding-whitespace requirement; brackets are unambiguous
	// Lite:  :+N           — require (whitespace-or-start) before to avoid false positives inside
	//                        normal punctuation (e.g. "e.g.:"). No lookahead needed after the digits.
	const fullRe = /\[(\d{2}):(\d{2}):(\d{2})\]/g;
	//const liteRe = /(?<=^|[\s])(:+)(\d{1,2})(?=[\s,;.!?]|$)/gm;
	//const liteRe = /(?<=^|[\s])(:+)(\d{1,2})(?=[^a-zA-Z]|$)/gm;
	const liteRe = /(?<=^|[\s])(:+)(\d{1,2})/gm;

	// Collect all matches with their index so we can process them in order.
	type Token =
		| {kind: "full"; index: number; hh: number; mm: number; ss: number}
		| {kind: "lite"; index: number; colons: number; seconds: number};

	const tokens: Token[] = [];

	for (const m of textBefore.matchAll(fullRe)) {
		tokens.push({kind: "full", index: m.index!, hh: +m[1], mm: +m[2], ss: +m[3]});
	}
	for (const m of textBefore.matchAll(liteRe)) {
		tokens.push({kind: "lite", index: m.index!, colons: m[1].length, seconds: +m[2]});
	}
	tokens.sort((a, b)=>a.index - b.index);

	let lastFullTimestampMs: number|null = null;
	let lastTimestampMs: number|null = null;

	for (const tok of tokens) {
		if (tok.kind === "full") {
			const baseDay = moment(entrySleepTime ?? Date.now()).startOf("day");
			let candidate = baseDay.clone().hours(tok.hh).minutes(tok.mm).seconds(tok.ss).valueOf();
			if (entrySleepTime != null && candidate < entrySleepTime) candidate += dayInMS;
			lastFullTimestampMs = candidate;
			lastTimestampMs = candidate;
		} else if (lastFullTimestampMs != null) {
			// lite: colons.length - 1 = minuteDiff from last full-timestamp's minute
			const minuteDiff = tok.colons - 1;
			const baseMinute = Math.floor(lastFullTimestampMs / minuteInMS);
			lastTimestampMs = (baseMinute + minuteDiff) * minuteInMS + tok.seconds * secondInMS;
		}
	}

	return lastTimestampMs;
}